import uuid from "../../../library/uuid.js";
import global from "../../global.js";
import ObjectHandle from "../ObjectHandle.js";
import {format_distance} from "./Utils.js";
const d3 = global.d3;
const log = global.log.getLogger("f_mtac.disk");
log.setLevel(log.levels.WARN);
const HANDLE_HIT_BOX_SIZE = () => (1 * global.app.scale.wb) / 5;

/**
 * @brief Class used for drawing the Disk, after the object Disk is abstracted into a Generic.
 */
const Disk = function (group, color) {
    //Initializing the data of Disk.
    this.data = {
        id: `local-${uuid()}`,
        center: {x: 0, y: 0},
        radius: 0,
        color: color,
        class: ["node", "disk", "object", "movable", "removable"], //Used to map properties/actions afterward (To Be Confirmed)
        storage: "object"
    };
    this.group = group;
    this.handle = null;
    this.node = null;
    this.circle = null;
    this.handle_resize_group = null;
    this.handle_resize = null;
    this.radius = null;
    this.position = {x: 0, y: 0};
    this.scene = global.scene;
};

Disk.prototype.render = d3
    .line()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(d3.curveLinear);
Disk.prototype.draw = function (x, y) {
    this.data.center.x = x;
    this.data.center.y = y;
    this.group.select(`#${this.data.id}`).remove();
    this.handle = this.group.append("g").attr("class", "handle").attr("id", this.data.id);
    this.node = this.handle.append("g").attr("class", this.data.class.join(" "));
    this.node.attr("data-type", "graphics");
    this.node.attr("data-subtype", "disk");
    this.circle = this.node
        .append("circle")
        .attr("cx", this.data.center.x)
        .attr("cy", this.data.center.y)
        .attr("r", this.data.radius)
        .attr("stroke", this.data.color)
        .attr("stroke-width", 3)
        .attr("stroke-opacity", 0.4)
        .attr("fill", this.data.color)
        .attr("fill-opacity", 0.2);
    this.position.x = x;
    this.position.y = y;
    this.handle_resize_group = this.node.append("g").attr("class", "handle_resize_group"); // Contain the handle used for resizing and the handle_resize
    this.handle_resize = this.handle_resize_group.append("polygon").attr("class", "handle_resize").attr("stroke", this.data.color).attr("stroke-width", 2).attr("fill", "none").attr("fill-opacity", 0); // Triangle used as the tip of the handle_resize
    this.radius = this.node
        .append("path")
        .attr("class", "radius")
        .attr("stroke", this.data.color)
        .attr("stroke-width", 3)
        .attr("stroke-opacity", 0.4)
        .attr(
            "d",
            this.render([
                [this.data.center.x, this.data.center.y],
                [this.position.x, this.position.y]
            ])
        );

    // distance is refering to radius real distance
    this.distance_group = this.node.append("g").attr("class", "distance_group");
    this.distance = this.distance_group.append("g").attr("class", "distance");
    this.distance_text_group = this.distance.append("g").attr("id", "distance_overlay");
    this.distance_frame = this.distance_text_group
        .append("rect")
        .attr("rx", global.app.scale.wb / 4)
        .attr("opacity", "50%")
        .attr("fill", this.data.color);
    this.distance_text = this.distance_text_group.append("text").attr("id", "distance_text");
    if (global.storage.selected_context.projection.project == null) {
        //grid scale not visible if sitac is not georeferenced
        this.scene.style.rule_set(
            "distance_group_visibility",
            `
            #${this.data.id} g.distance_group {visibility: hidden;}
        `
        );
    } else {
        this.scene.style.rule_set(
            "distance_group_visibility",
            `
        #${this.data.id} g.distance_group {visibility: visible;}
    `
        );
    }
};
Disk.prototype.update = function (x, y) {
    // Called during creation to update the coordinates of the Disk repeatedly
    log.debug("Disk.update IN :");
    this.data.radius = Math.sqrt(Math.pow(x - this.data.center.x, 2) + Math.pow(y - this.data.center.y, 2));

    this.circle.attr("r", this.data.radius);
    this.radius.attr(
        "d",
        this.render([
            [this.data.center.x, this.data.center.y],
            [this.position.x, this.position.y]
        ])
    );

    this.position.x = x;
    this.position.y = y;

    log.debug([this.data.center.x, this.data.center.y]);
    log.debug([this.position.x, this.position.y]);
    // Compute the real world radius distance
    let computed_distance = global.view.get_distance_meter(
            [this.data.center.x, this.data.center.y], 
            [this.position.x, this.position.y]
    );

    if (computed_distance !== undefined) { // i.e. if a projection is defined for the view
        log.debug(computed_distance);
        this.distance_text.text(format_distance(computed_distance));
    }

    // Calculate the middle to place the distance on it
    let middle_radius = {};
    middle_radius.x = (this.position.x + this.data.center.x) / 2;
    middle_radius.y = (this.position.y + this.data.center.y) / 2;
    // Fetch the dimension of the box to prepare the frame
    var {width: w, height: h, x1, y1} = this.distance_text.node().getBBox();
    // Move the text and frame a little bit to center it on the line
    x1 = middle_radius.x - w / 2;
    y1 = middle_radius.y + h / 3; // the division here work fine (tested to find this value)
    this.distance_text.attr("x", x1).attr("y", y1);
    // Padding
    h += 6;
    w += 18;
    x1 = middle_radius.x - w / 2;
    y1 = middle_radius.y - h / 2;
    this.distance_frame.attr("x", x1).attr("y", y1).attr("width", w).attr("height", h);
    // Transform-origin is necessary here for the scale on zoom and dezoom
    this.distance.attr("transform-origin", `${middle_radius.x}px ${middle_radius.y}px`);

    // Angle is used for rotating the handle_resize on itself
    const get_angle = (dx, dy) => {
        var angle;
        if (dx === 0) {
            if (dy < 0) {
                angle = -Math.PI / 2;
            } else {
                angle = Math.PI / 2;
            }
        } else if (dx > 0) {
            angle = Math.atan(dy / dx);
        } else if (dx < 0) {
            angle = Math.atan(dy / dx) - Math.PI;
        }
        angle = (angle / (2 * Math.PI)) * 360;
        return angle;
    };

    let x0 = this.position.x,
        y0 = this.position.y;

    /**
     *                      (-a, +b)
     *                     |\
     * |-----  R - a ------| \
     * h                   |  |
     * |-------------------| /
     *                     |/
     *                      (-a, -b)
     */
    const a = 60,
        b = 40,
        sw = 3;

    // Used for drawing the svg
    let handle_resize_points = [
        [x0 + a, y0],
        [x0, y0 - b],
        [x0 - a, y0],
        [x0, y0 + b]
    ];

    let dx = x0 - this.data.center.x,
        dy = y0 - this.data.center.y;
    let angle = get_angle(dx, dy);

    this.handle_resize_group.attr("transform", `rotate(${angle})`).attr("transform-origin", `${x0}px ${y0}px`);

    this.handle_resize.attr("stroke", this.data.color).attr("stroke-width", sw).attr("fill", this.data.color).attr("fill-opacity", 1).attr("points", handle_resize_points).attr("transform-origin", `${x0}px ${y0}px`);
    this.distance.attr("transform-box", "fill-box");
};
Disk.prototype.update_radius = function (r) {
    // Need to check if it's called
    // Used only during creation to update the radius(visual element) to match the radius of the Disk
    log.debug("Disk.update_radius IN :");
    this.position.x = this.data.center.x + ((this.position.x - this.data.center.x) / this.data.radius) * r;
    this.position.y = this.data.center.y + ((this.position.y - this.data.center.y) / this.data.radius) * r;
    this.data.radius = r;
    this.circle.attr("r", this.data.radius);
    this.radius.attr(
        "d",
        this.render([
            [this.data.center.x, this.data.center.y],
            [this.position.x, this.position.y]
        ])
    );
};
Disk.prototype.end = function () {
    // Handle to move/select the Disk
    let object_handle = ObjectHandle({
        x: this.data.center.x,
        y: this.data.center.y,
        color: this.data.color
    });

    this.node.append(() => object_handle.node());
    this.handle_resize_group.append("circle").attr("class", "object_handle_hit_box").attr("cx", this.position.x).attr("cy", this.position.y).attr("r", HANDLE_HIT_BOX_SIZE()).attr("fill", "none");
    this.scene.style.rule_set(
        "distance_group_visibility",
        `
        #${this.data.id} g.distance_group {visibility: hidden;}
    `
    );
    return Promise.resolve();
};
Disk.prototype.is_empty = function () {
    return !this.center.x && !this.center.y;
};
Disk.prototype.points = function (interval) {
    var circle_node = this.circle.node();
    var circle_length = circle_node.getTotalLength();
    var points_number = circle_length / interval;
    return d3.range(points_number).map((d) => circle_node.getPointAtLength(d * interval));
};

Disk.prototype.detach = function () {
    this.handle = null;
    this.node = null;
    this.circle = null;
    this.radius = null;
    this.handle_resize_group = null;
};
Disk.prototype.export = function () {
    return this.handle;
};

export default Disk;
